"use client";

import { type ReactNode, createContext, useContext } from "react";
import { I18nProvider, useLocale as useReactAriaLocale } from "react-aria";
import { resolveDisplayLocale } from "../formatters/resolve-display-locale";

type DesignSystemLocaleContextValue = {
	locale: string;
};

const DesignSystemLocaleContext = createContext<DesignSystemLocaleContextValue | null>(null);

type DesignSystemLocaleProviderProps = {
	children: ReactNode;
	companyLocale?: string;
	language?: string;
};

export const DesignSystemLocaleProvider = ({ children, companyLocale, language }: DesignSystemLocaleProviderProps) => {
	const { locale: reactAriaLocale } = useReactAriaLocale();
	const locale = resolveDisplayLocale({ language, companyLocale, fallbackLocale: reactAriaLocale });

	return (
		<DesignSystemLocaleContext.Provider value={{ locale }}>
			<I18nProvider locale={locale}>{children}</I18nProvider>
		</DesignSystemLocaleContext.Provider>
	);
};

type UseLocaleProps = {
	locale: string;
};

export const useLocale = (): UseLocaleProps => {
	const context = useContext(DesignSystemLocaleContext);
	if (!context) {
		throw new Error("useLocale must be used within a DesignSystemLocaleProvider");
	}

	return context;
};
